// ---------------------------------------------------------------------------
// Taking payment.
//
// The cashier builds up a list of payments - twenty dollars cash, the rest on
// Visa - and the dialog keeps a running "still owed" figure. The complete
// button stays disabled until the total is covered, so an underpaid sale
// cannot be submitted by accident. The server checks the same thing again.
//
// Every amount is handled as a decimal string. Lira are converted at the rate
// currently in force, which is also the rate the server will freeze onto the
// sale.
//
// Double submission is guarded three ways: the button disables itself while
// the request is in flight, the idempotency key is generated once when the
// dialog opens rather than per click, and the server recognises a repeat.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  Banknote,
  Check,
  CreditCard,
  Plus,
  ShoppingBag,
  Smartphone,
  Trash2,
  Truck,
  UserCheck,
  UtensilsCrossed,
} from 'lucide-react';
import clsx from 'clsx';
import Decimal from 'decimal.js';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { formatLbp, formatUsd, toDecimal, usdToLbpDisplay } from '../../utils/format';
import { settleInLira } from '../../utils/settlement';
import type { Currency, Customer, PaymentDraft, PaymentMethod } from '../../types';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  /// The dialog's heading. Defaults to the ordinary "take payment" title; the
  /// delivery account collection names the ORDER instead, so the cashier can
  /// see whose money they are recording before they record it.
  title?: string;
  /// A line above the figures the cashier must read before taking money -
  /// the account collection uses it to say WHOSE drawer the money joins.
  notice?: ReactNode;
  /// The order total in USD, as a decimal string. What the payments reconcile
  /// against, and what the server charges in its accounts.
  totalUsd: string;
  /// The SAME total in whole lira - the figure the cashier reads out and the
  /// customer counts. Passed in rather than converted here: rebuilding it from
  /// the rounded dollar total asked for a different amount than the Current
  /// Order had just shown. Absent only where no lira total exists.
  totalLbp?: string | null;
  exchangeRate: string | null;
  submitting: boolean;
  /// A failure message from the server, shown inline so the cashier can act.
  errorMessage: string | null;
  /// Phase 7. Absent unless the cashier holds `payments.credit`; without it
  /// the account button is never drawn and the server refuses it anyway.
  creditAllowed?: boolean;
  /// The customer this sale is for, when one has been chosen. Credit needs
  /// somebody to owe it.
  customer?: Customer | null;
  onChooseCustomer?: () => void;
  /// True when this sale carries a delivery fee (or pays a delivery order):
  /// the fulfilment is then DELIVERY, shown read-only, with no choice.
  deliveryActive?: boolean;
  /// The fee, for the read-only delivery row. Display only.
  deliveryFeeLbp?: string | null;
  /// Set when this payment settles an existing ORDER - the order decided its
  /// own fulfilment when it was taken, so the dialog shows it and asks
  /// nothing.
  orderFulfilment?: 'PICKUP' | 'DELIVERY' | null;
  /// True when at least one line routes to a preparation bench - the only
  /// case where "no preparation" means anything. A drinks-only order never
  /// sees the checkbox.
  hasPreparationItems?: boolean;
  /// True when this payment settles an order whose preparation was ALREADY
  /// dispatched. A checkbox promising "no preparation" after the ticket has
  /// been cut would be a lie; the truthful status line shows instead.
  preparationAlreadySent?: boolean;
  onSubmit: (
    payments: PaymentDraft[],
    changeCurrency: Currency,
    /// How the order reaches the customer. Null when an existing order (or
    /// the fee) already decided - the server derives it there.
    reach: { fulfilment: 'PICKUP' | 'ON_TABLE' | 'DELIVERY'; tableLabel: string | null } | null,
    /// The cashier's explicit "no preparation" for THIS order only.
    skipPreparation: boolean,
    /// The cashier's «بدون إيصال» for THIS sale only - false unless ticked.
    skipReceipt: boolean,
  ) => void;
  /// Whether settling this payment prints a customer receipt at all. A
  /// delivery's account collected later prints nothing (its paper left with
  /// the driver), so «بدون إيصال» would decide nothing there and is not
  /// shown. Every till sale and every pickup prints one: the default.
  receiptWillPrint?: boolean;
}

const METHOD_ICONS: Record<PaymentMethod, typeof Banknote> = {
  CASH: Banknote,
  VISA: CreditCard,
  WHISH: Smartphone,
  CREDIT: UserCheck,
};

export function PaymentDialog({
  open,
  onClose,
  title,
  notice,
  totalUsd,
  totalLbp,
  exchangeRate,
  submitting,
  errorMessage,
  creditAllowed = false,
  customer = null,
  onChooseCustomer,
  deliveryActive = false,
  deliveryFeeLbp = null,
  orderFulfilment = null,
  hasPreparationItems = false,
  receiptWillPrint = true,
  preparationAlreadySent = false,
  onSubmit,
}: PaymentDialogProps) {
  const { t, format } = useI18n();

  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [changeCurrency, setChangeCurrency] = useState<Currency>('USD');

  /// HOW THE ORDER REACHES THE CUSTOMER - the choice this dialog now owns.
  /// PICKUP by default: the overwhelmingly common counter case, and shown
  /// selected rather than hidden so the default is a visible fact, not a
  /// silent assumption. Ignored entirely while a delivery fee or an existing
  /// order decides instead.
  const [fulfilment, setFulfilment] = useState<'PICKUP' | 'ON_TABLE'>('PICKUP');
  const [tableLabel, setTableLabel] = useState('');
  /// "بدون تحضير" - an ORDER-LEVEL exception, never a preference. Reset on
  /// every open, so one customer's skip can never leak into the next.
  const [skipPreparation, setSkipPreparation] = useState(false);
  /// «بدون إيصال» - the same per-sale exception for the cashier's printer.
  /// Unticked by default and on every open: the receipt prints unless the
  /// cashier says otherwise for this one sale.
  const [skipReceipt, setSkipReceipt] = useState(false);

  // The dollars shown: the lira bill's price, quoted once (divided by the
  // rate, rounded to the cent) - the figure the server settles at. A caller's
  // own dollar total (an order's stored estimate, the sum of its rounded
  // dollar lines) can sit a cent off that, and the cashier must never be
  // asked for a figure the server will refuse.
  const total = totalLbp !== null && totalLbp !== undefined && exchangeRate
    ? toDecimal(totalLbp).dividedBy(toDecimal(exchangeRate)).toDecimalPlaces(2)
    : toDecimal(totalUsd);

  // Reset whenever the dialog opens, so a previous attempt never leaks in.
  useEffect(() => {
    if (!open) return;
    setPayments([]);
    setMethod('CASH');
    setCurrency('USD');
    setAmount('');
    setReference('');
    setChangeCurrency('USD');
    setFulfilment('PICKUP');
    setTableLabel('');
    setSkipPreparation(false);
    setSkipReceipt(false);
  }, [open]);

  /// Everything paid so far, expressed in USD.
  const paidUsd = useMemo(() => {
    return payments.reduce((sum, payment) => {
      const value = toDecimal(payment.amount);
      const inUsd =
        payment.currency === 'USD'
          ? value
          : exchangeRate
            ? value.dividedBy(toDecimal(exchangeRate))
            : new Decimal(0);
      return sum.plus(inUsd);
    }, new Decimal(0));
  }, [payments, exchangeRate]);

  // THE SETTLEMENT, by the server's own rule when the lira bill is known:
  // lira tenders come off the lira bill exactly; dollar tenders must cover
  // the cent-quoted price of what is left. Complete Sale is gated on this,
  // so what the dialog calls covered the server records - no phantom cent
  // between the two. Without a lira bill (no rate, a legacy caller) the
  // dollar comparison stands as before.
  const liraSettlement = useMemo(
    () => (totalLbp !== null && totalLbp !== undefined && exchangeRate ? settleInLira(totalLbp, exchangeRate, payments) : null),
    [totalLbp, exchangeRate, payments],
  );
  const remaining = liraSettlement ? liraSettlement.remainingUsd : total.minus(paidUsd);
  const covered = liraSettlement ? liraSettlement.covered : remaining.lessThanOrEqualTo(0);
  const change = liraSettlement ? liraSettlement.changeUsd : covered ? remaining.negated().toDecimalPlaces(2) : new Decimal(0);

  /// What is still owed, in whichever currency is selected - so pressing
  /// "exact amount" fills the right figure.
  const exactForCurrency = useMemo(() => {
    const outstanding = remaining.greaterThan(0) ? remaining : new Decimal(0);
    if (currency === 'USD') return outstanding.toDecimalPlaces(2).toFixed(2);
    if (!exchangeRate) return '';
    if (liraSettlement) {
      // The exact lira still owed - never a rounded dollar total converted
      // back, which would turn an exact 480,000 LBP bill into 479,720 LBP.
      return liraSettlement.remainingLbp.toFixed(0);
    }
    return outstanding.times(toDecimal(exchangeRate)).toDecimalPlaces(0).toFixed(0);
  }, [remaining, currency, exchangeRate, liraSettlement]);

  // --- selling on account, Phase 7 ---------------------------------------
  const creditUsd = useMemo(
    () =>
      payments
        .filter((payment) => payment.method === 'CREDIT')
        .reduce((sum, payment) => sum.plus(toDecimal(payment.amount)), new Decimal(0)),
    [payments],
  );

  const sellingOnCredit = creditUsd.greaterThan(0);
  const headroom = customer?.balance ? toDecimal(customer.balance.availableCreditUsd) : null;

  /// Every reason a credit line cannot be added right now, in the order the
  /// shop would say them out loud. The server checks all of this again.
  const creditProblem = useMemo(() => {
    if (method !== 'CREDIT') return null;
    if (!customer) return t.pos.creditNeedsCustomer;
    if (!customer.balance) return null;
    if (!customer.balance.canBuyOnCredit) return customer.balance.blockedReason;

    const wanted = amount.trim() ? toDecimal(amount) : new Decimal(0);
    if (headroom && wanted.greaterThan(headroom)) {
      return format(t.pos.creditNotEnough, { available: formatUsd(headroom) });
    }
    return null;
  }, [method, customer, amount, headroom, t, format]);

  /// Credit tops up what is left; handing back cash against a promise would be
  /// the shop giving money away. The server refuses it too.
  const creditWouldOverpay =
    method === 'CREDIT' && amount.trim().length > 0 && toDecimal(amount).greaterThan(remaining);

  const canAdd =
    amount.trim().length > 0 &&
    toDecimal(amount).greaterThan(0) &&
    creditProblem === null &&
    !creditWouldOverpay;

  const addPayment = () => {
    if (!canAdd) return;

    setPayments((current) => [
      ...current,
      {
        method,
        currency,
        amount: toDecimal(amount).toDecimalPlaces(currency === 'USD' ? 2 : 0).toFixed(currency === 'USD' ? 2 : 0),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      },
    ]);

    setAmount('');
    setReference('');
  };

  const removePayment = (index: number) => {
    setPayments((current) => current.filter((_, position) => position !== index));
  };

  /// The one fulfilment rule the dialog enforces itself: an on-table order
  /// must say which table BEFORE the sale can complete. The server enforces
  /// the same rule again.
  const fulfilmentDecided = deliveryActive || Boolean(orderFulfilment);
  /// STAGE 2 exists only when the canonical settlement says the total is
  /// covered by at least one real payment - the exact condition that gates
  /// Complete Sale, so the section and the button can never disagree about
  /// whether the money is in.
  const stageTwo = covered && payments.length > 0;
  const tableMissing = !fulfilmentDecided && fulfilment === 'ON_TABLE' && !tableLabel.trim();
  const canComplete = covered && payments.length > 0 && !submitting && !tableMissing;

  const submit = () => {
    if (!canComplete) return;
    onSubmit(
      payments,
      changeCurrency,
      // The fee or the order decides for itself; the server derives it
      // there. Otherwise: exactly what the cashier chose.
      fulfilmentDecided
        ? null
        : fulfilment === 'ON_TABLE'
          ? { fulfilment: 'ON_TABLE', tableLabel: tableLabel.trim() }
          : { fulfilment: 'PICKUP', tableLabel: null },
      skipPreparation && hasPreparationItems && !preparationAlreadySent,
      skipReceipt && receiptWillPrint,
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? t.payment.title}
      size="md"
      guardUnsaved
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={!canComplete}
          >
            {submitting ? t.payment.completing : t.payment.complete}
          </button>
        </>
      }
    >
      {notice}

      {/* --- what is owed --------------------------------------------- */}
      <div className="mb-4 rounded-card border border-line bg-surface-soft p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-ink">{t.payment.dueNow}</span>
          <span className="numeric text-2xl font-bold text-butcher-green">{formatUsd(total)}</span>
        </div>

        {exchangeRate ? (
          <div className="mt-1 flex items-baseline justify-between text-xs text-ink-muted">
            <span>{t.receipt.inLbp}</span>
            <span className="numeric">
              {totalLbp ? formatLbp(totalLbp) : usdToLbpDisplay(total, exchangeRate)}
            </span>
          </div>
        ) : (
          <p className="mt-2 flex items-start gap-2 text-xs text-burgundy-red">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t.payment.noRate}
          </p>
        )}

        <div className="mt-3 space-y-1 border-t border-line pt-3 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-muted">{t.payment.paidSoFar}</span>
            <span className="numeric font-semibold text-ink">{formatUsd(paidUsd)}</span>
          </div>

          {covered ? (
            change.greaterThan(0) ? (
              <div className="flex justify-between">
                <span className="font-medium text-ink">{t.payment.change}</span>
                <span className="numeric font-bold text-antique-gold">
                  {changeCurrency === 'USD'
                    ? formatUsd(change)
                    : exchangeRate
                      ? usdToLbpDisplay(change, exchangeRate)
                      : formatUsd(change)}
                </span>
              </div>
            ) : null
          ) : (
            <div className="flex justify-between">
              <span className="font-medium text-burgundy-red">{t.payment.remaining}</span>
              <span className="numeric font-bold text-burgundy-red">{formatUsd(remaining)}</span>
            </div>
          )}
        </div>
      </div>

      {/* --- payments taken so far ------------------------------------ */}
      {payments.length > 0 ? (
        <ul className="mb-4 space-y-1.5">
          {payments.map((payment, index) => {
            const Icon = METHOD_ICONS[payment.method];
            return (
              <li
                key={`${payment.method}-${payment.currency}-${index}`}
                className="flex items-center gap-2.5 rounded-control border border-line bg-surface px-3 py-2"
              >
                <Icon className="h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" />
                <span className="text-sm font-medium text-ink">
                  {payment.method === 'CASH'
                    ? t.payment.cash
                    : payment.method === 'VISA'
                      ? t.payment.visa
                      : payment.method === 'WHISH'
                        ? t.payment.whish
                        : t.payment.credit}
                </span>
                <span className="numeric ms-auto text-sm font-semibold text-ink">
                  {payment.currency === 'USD' ? formatUsd(payment.amount) : formatLbp(payment.amount)}
                </span>
                <button
                  type="button"
                  onClick={() => removePayment(index)}
                  disabled={submitting}
                  className="rounded p-1 text-burgundy-red transition-colors hover:bg-burgundy-red/10"
                  aria-label={t.payment.remove}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* --- add a payment --------------------------------------------- */}
      {!covered ? (
        <div className="rounded-card border border-line p-3">
          <p className="mb-2.5 text-sm font-semibold text-ink">{t.payment.addPayment}</p>

          {/* method. The account button only exists for somebody holding
              payments.credit - and the server refuses it regardless. */}
          <div
            className={clsx('mb-2.5 grid gap-1.5', creditAllowed ? 'grid-cols-2' : 'grid-cols-3')}
            role="group"
            aria-label={t.payment.method}
          >
            {(creditAllowed
              ? (['CASH', 'VISA', 'WHISH', 'CREDIT'] as const)
              : (['CASH', 'VISA', 'WHISH'] as const)
            ).map((option) => {
              const Icon = METHOD_ICONS[option];
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setMethod(option);
                    // An account balance is kept in dollars, so a credit line
                    // is always in dollars.
                    if (option === 'CREDIT') setCurrency('USD');
                  }}
                  aria-pressed={method === option}
                  className={clsx(
                    'flex h-11 items-center justify-center gap-1.5 rounded-control border text-sm font-semibold transition-colors',
                    method === option
                      ? 'border-butcher-green bg-butcher-green text-white'
                      : 'border-line-strong bg-surface text-ink hover:bg-surface-sunken',
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {option === 'CASH'
                    ? t.payment.cash
                    : option === 'VISA'
                      ? t.payment.visa
                      : option === 'WHISH'
                        ? t.payment.whish
                        : t.payment.credit}
                </button>
              );
            })}
          </div>

          {/* --- who owes it ------------------------------------------- */}
          {method === 'CREDIT' ? (
            <div className="mb-2.5 rounded-control border border-antique-gold/40 bg-antique-gold/10 p-3">
              {customer ? (
                <>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-ink">{customer.name}</span>
                    {onChooseCustomer ? (
                      <button
                        type="button"
                        onClick={onChooseCustomer}
                        className="text-xs text-butcher-green underline-offset-2 hover:underline"
                      >
                        {t.pos.changeCustomer}
                      </button>
                    ) : null}
                  </div>

                  {customer.balance ? (
                    <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs">
                      <span className="text-ink-muted">
                        {t.credit.balance}{' '}
                        <span className="numeric font-semibold text-ink">
                          {formatUsd(customer.balance.balanceUsd)}
                        </span>
                      </span>
                      <span className="text-ink-muted">
                        {t.credit.available}{' '}
                        <span className="numeric font-semibold text-butcher-green">
                          {formatUsd(customer.balance.availableCreditUsd)}
                        </span>
                      </span>
                    </div>
                  ) : null}
                </>
              ) : (
                <button
                  type="button"
                  onClick={onChooseCustomer}
                  className="flex w-full items-center justify-center gap-2 text-sm font-medium text-ink"
                >
                  <UserCheck className="h-4 w-4" aria-hidden="true" />
                  {t.pos.chooseCustomer}
                </button>
              )}
            </div>
          ) : null}

          {/* currency. A credit line is always in dollars. */}
          {method === 'CREDIT' ? null : (
            <div className="mb-2.5 grid grid-cols-2 gap-1.5" role="group" aria-label={t.payment.currency}>
              {(['USD', 'LBP'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setCurrency(option)}
                  disabled={option === 'LBP' && !exchangeRate}
                  aria-pressed={currency === option}
                  className={clsx(
                    'h-10 rounded-control border text-sm font-semibold transition-colors disabled:opacity-40',
                    currency === option
                      ? 'border-butcher-green bg-butcher-green/10 text-butcher-green'
                      : 'border-line-strong bg-surface text-ink-muted hover:bg-surface-sunken',
                  )}
                >
                  {option === 'USD' ? 'USD $' : 'LBP'}
                </button>
              ))}
            </div>
          )}

          {/* amount */}
          <label htmlFor="payment-amount" className="label text-xs">
            {t.payment.amount}
          </label>
          <div className="flex gap-2">
            <input
              id="payment-amount"
              type="text"
              inputMode="decimal"
              dir="ltr"
              value={amount}
              onChange={(event) => {
                const next = event.target.value;
                if (next === '' || /^\d*\.?\d*$/.test(next)) setAmount(next);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addPayment();
                }
              }}
              placeholder="0.00"
              className="input numeric flex-1"
            />
            <button
              type="button"
              onClick={() => setAmount(exactForCurrency)}
              className="btn-secondary shrink-0 whitespace-nowrap"
            >
              {t.payment.quickExact}
            </button>
          </div>

          {/* reference, only useful for a card or app payment. Cash has no
              approval code, and neither does putting it on an account. */}
          {method === 'VISA' || method === 'WHISH' ? (
            <div className="mt-2.5">
              <label htmlFor="payment-reference" className="label text-xs">
                {t.payment.reference}
              </label>
              <input
                id="payment-reference"
                type="text"
                dir="ltr"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                className="input"
                maxLength={100}
              />
              <p className="mt-1 text-xs text-ink-muted">{t.payment.referenceHint}</p>
            </div>
          ) : null}

          {/* Why the account line cannot be added, said before the button is
              pressed rather than after the server refuses it. */}
          {creditProblem ? (
            <p
              className="mt-2.5 flex items-start gap-2 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-2.5 text-xs"
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-burgundy-red" aria-hidden="true" />
              <span className="text-ink">{creditProblem}</span>
            </p>
          ) : null}

          {creditWouldOverpay ? (
            <p
              className="mt-2.5 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-2.5 text-xs text-ink"
              role="alert"
            >
              {t.pos.creditNoChange}
            </p>
          ) : null}

          <button
            type="button"
            onClick={addPayment}
            disabled={!canAdd}
            className="btn-primary mt-3 w-full"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t.payment.addPayment}
          </button>
        </div>
      ) : null}

      {/* Part of this sale is going on the account, so say whose. */}
      {sellingOnCredit && customer ? (
        <p className="mt-4 rounded-card border border-antique-gold/40 bg-antique-gold/10 p-3 text-sm text-ink">
          {format(t.pos.goingOnAccount, {
            amount: formatUsd(creditUsd),
            name: customer.name,
          })}
        </p>
      ) : null}

      {/* --- which currency the change is handed back in ----------------
          Never offered on a credit sale: the shop does not hand cash back
          against a promise. */}
      {covered && change.greaterThan(0) && exchangeRate && !sellingOnCredit ? (
        <div className="mt-4 rounded-card border border-line p-3">
          <p className="mb-2 text-sm font-semibold text-ink">{t.payment.changeCurrency}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {(['USD', 'LBP'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setChangeCurrency(option)}
                aria-pressed={changeCurrency === option}
                className={clsx(
                  'h-10 rounded-control border text-sm font-semibold transition-colors',
                  changeCurrency === option
                    ? 'border-antique-gold bg-antique-gold/15 text-charcoal'
                    : 'border-line-strong bg-surface text-ink-muted hover:bg-surface-sunken',
                )}
              >
                {option === 'USD' ? formatUsd(change) : usdToLbpDisplay(change, exchangeRate)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* --- a failure from the server --------------------------------- */}
      {/* --- STAGE 2: how the order reaches the customer ------------------
          Rendered ONLY once the payments cover the total - the same
          `covered` the settlement contract computes, never a second
          arithmetic. While money is still owed the cashier is doing one job,
          and this section is not part of it: not shown, not disabled, not
          there. Deleting a payment un-covers the total and the section goes
          away again; the choice made so far stays in the dialog's own state,
          so covering the total once more brings it back as it was - remembered
          for convenience, but never persisted while payment is incomplete,
          because Complete Sale is gated on `covered` too. */}
      {stageTwo ? (
        <>
          {/* The line that marks the stage change: the money is in, and what
              remains is saying how the order leaves the shop. */}
          <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-butcher-green" role="status">
            <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t.payment.paidInFull}
          </p>

      <div className="mt-2 rounded-card border border-line bg-surface-soft p-4">
        <p className="text-sm font-medium text-ink">
          {t.payment.fulfilmentLabel}
          {!fulfilmentDecided ? <span className="ms-1 text-burgundy-red">*</span> : null}
        </p>

        {fulfilmentDecided ? (
          // Decided by the fee or by the order being paid: shown, never
          // asked. A cashier cannot un-deliver a sale that charges for
          // delivery - they remove the fee instead.
          <div className="mt-2">
            <p className="inline-flex items-center gap-2 rounded-control border-2 border-butcher-green bg-butcher-green/10 px-3 py-2 text-sm font-semibold text-ink">
              {orderFulfilment === 'PICKUP' ? (
                <ShoppingBag className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Truck className="h-4 w-4" aria-hidden="true" />
              )}
              {orderFulfilment === 'PICKUP'
                ? t.payment.fulfilmentPickup
                : t.payment.fulfilmentDelivery}
            </p>
            {deliveryActive && deliveryFeeLbp ? (
              <p className="mt-1.5 text-xs text-ink-muted">
                {t.payment.deliveryLocked}{' '}
                <span className="numeric" dir="ltr">
                  {formatLbp(deliveryFeeLbp)}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label={t.payment.fulfilmentLabel}>
              {(
                [
                  { value: 'PICKUP' as const, label: t.payment.fulfilmentPickup, Icon: ShoppingBag },
                  { value: 'ON_TABLE' as const, label: t.payment.fulfilmentOnTable, Icon: UtensilsCrossed },
                ]
              ).map(({ value, label, Icon }) => {
                const selected = fulfilment === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setFulfilment(value);
                      // Switching away from the table clears the table: a
                      // pickup carrying table 4 is the contradiction the
                      // whole feature exists to prevent.
                      if (value === 'PICKUP') setTableLabel('');
                    }}
                    className={clsx(
                      'flex items-center justify-center gap-2 rounded-control border-2 px-3 py-2.5 text-sm font-semibold transition-colors',
                      selected
                        ? 'border-butcher-green bg-butcher-green/10 text-ink'
                        : 'border-line bg-white text-ink-muted hover:border-line-strong',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {label}
                    {selected ? <Check className="h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>

            {fulfilment === 'ON_TABLE' ? (
              <div className="mt-2.5">
                <label htmlFor="pay-table" className="label">
                  {t.payment.tableLabel} <span className="text-burgundy-red">*</span>
                </label>
                <input
                  id="pay-table"
                  className="input numeric"
                  value={tableLabel}
                  onChange={(event) => setTableLabel(event.target.value)}
                  placeholder={t.payment.tablePlaceholder}
                  maxLength={30}
                  autoFocus
                />
                {tableMissing ? (
                  <p className="field-error" role="alert">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {t.payment.tableRequired}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>

          {/* --- the order-level preparation exception --------------------
              Only when the order would actually reach a bench: a checkbox
              reading "no preparation" over a drinks-only order decides
              nothing and teaches cashiers to ignore it. And only BEFORE
              dispatch - an order whose ticket was already cut gets the
              truth, not a checkbox that pretends to un-send it. */}
          {hasPreparationItems && preparationAlreadySent ? (
            <p className="mt-3 rounded-control border border-line bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
              {t.payment.preparationAlreadySent}
            </p>
          ) : null}

          {hasPreparationItems && !preparationAlreadySent ? (
            <label
              className={clsx(
                'mt-3 flex cursor-pointer items-start gap-2.5 rounded-card border-2 p-3 transition-colors duration-150 motion-reduce:transition-none',
                skipPreparation
                  ? 'border-antique-gold bg-antique-gold/10'
                  : 'border-line bg-surface-soft hover:border-line-strong',
              )}
            >
              <input
                type="checkbox"
                checked={skipPreparation}
                onChange={(event) => setSkipPreparation(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line-strong text-butcher-green focus:ring-antique-gold"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">
                  {t.payment.skipPreparation}
                </span>
                {skipPreparation ? (
                  <span
                    className="mt-0.5 flex items-start gap-1 text-xs font-medium text-antique-gold-deep"
                    role="status"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {t.payment.skipPreparationWarning}
                  </span>
                ) : (
                  <span className="mt-0.5 block text-xs text-ink-muted">
                    {t.payment.skipPreparationHint}
                  </span>
                )}
              </span>
            </label>
          ) : null}

          {/* «بدون إيصال» - on EVERY sale that prints a receipt, whatever it
              contains: the cashier's printer serves every order, unlike the
              benches. Independent of «بدون تحضير»: skipping one never skips
              the other. The sale is recorded either way, and the receipt can
              be reprinted by hand from Orders. */}
          {receiptWillPrint ? (
            <label
              className={clsx(
                'mt-3 flex cursor-pointer items-start gap-2.5 rounded-card border-2 p-3 transition-colors duration-150 motion-reduce:transition-none',
                skipReceipt
                  ? 'border-antique-gold bg-antique-gold/10'
                  : 'border-line bg-surface-soft hover:border-line-strong',
              )}
            >
              <input
                type="checkbox"
                checked={skipReceipt}
                onChange={(event) => setSkipReceipt(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line-strong text-butcher-green focus:ring-antique-gold"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">
                  {t.payment.skipReceipt}
                </span>
                {skipReceipt ? (
                  <span
                    className="mt-0.5 flex items-start gap-1 text-xs font-medium text-antique-gold-deep"
                    role="status"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {t.payment.skipReceiptWarning}
                  </span>
                ) : (
                  <span className="mt-0.5 block text-xs text-ink-muted">
                    {t.payment.skipReceiptHint}
                  </span>
                )}
              </span>
            </label>
          ) : null}
        </>
      ) : null}

      {errorMessage ? (
        <div
          className="mt-4 flex gap-2.5 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
          <div>
            <p className="font-semibold text-burgundy-red">{t.payment.failedTitle}</p>
            <p className="mt-0.5 text-ink">{errorMessage}</p>
            <p className="mt-1 text-xs text-ink-muted">{t.payment.failedRetry}</p>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
